import React, { useEffect, useState, useRef, Suspense } from 'react';
import {
  unstable_HistoryRouter as Router,
  Routes,
  Route,
  useNavigate,
  Navigate,
  useSearchParams
} from 'react-router-dom';
import { EventType, InteractionStatus, InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication, AuthenticatedTemplate, useIsAuthenticated } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { Snackbar, Alert, Button } from '@mui/material';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { LandingPage } from './Routes/LandingPage';
import { CandidatePortal } from './Routes/CandidatePortal';
import { Dashboard } from './Routes/CandidatePages/Dashboard';
import { ErrorPage } from './Routes/errorpages/ErrorPage';
// eslint-disable-next-line import/no-cycle
import {
  LandingContent,
  Jobs,
  Settings,
  Tifo,
  TimesheetsPage,
  ProfileBuilderOne,
  ProfileBuilderTwo,
  ProfileBuilderThree,
  ProfileBuilderFour,
  ProfileBuilderFive,
  ProfileBuilderSix,
  ProfileBuilderSeven,
  MyProfile,
  ExpensesPage,
  TimesheetPage,
  UnsubscribePage,
  QuickApplyEmbedded
} from './Routes';
import { backendAuth, backendUser } from './Api';
import './scss/App.scss';
import RightToBeForgotten from './Routes/rightToBeForgotten/RightToBeForgotten';
import configData from './config.json';
import { fetchUserData } from './actions/user';
import { checkOnboardingCookie } from './utils/helpers';
import api from './api/apiSettings';
import { appInsights, reactPlugin } from './services/ApplicationInsightsService';
import { getConstants } from './actions/constFromApi';
import { handleLogin } from './utils/loginFunctions';
import AppNotification from './Components/common/AppNotification/AppNotification';
import { loginRequest } from './authConfig';
import { setAllowedParts } from './actions/accessToParts';
import CreateTNEPage from './Routes/CandidatePages/CreateTNEPage/CreateTNEPage';
import { ACCESS_TOKEN_NAME, PAGE_TYPES, generalEnums, idTokenClaims } from './utils/enums';
import ExpensePage from './Routes/CandidatePages/ExpensePage/ExpensePage';
import InteractionPage from './Routes/InteractionPage/InteractionPage';
import history from './utils/history';
import LoadingWithSpinner from './Components/common/LoadingWithSpinner/LoadingWithSpinner';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import MainLayout from './layouts/MainLayout';
import QuickApplyLayout from './layouts/QuickApplyLayout';
import { logAppError, logAppEvent, logAuthStep, logBackendStep } from './utils/logActions';
import OnboardingInteraction from './Routes/InteractionPage/onboardingInteractionPage';

// PWA Components
import InstallPWA from './Components/InstallPWA';
import OfflineBanner from './Components/OfflineBanner';
import { useNetworkStatus } from './utils/useNetworkStatus';
import { setUpdateAvailable } from './actions/pwa';
import { isStandalone } from './utils/pwa';

const PRELOAD_RELOAD_KEY = 'pwa-preload-reload';

function App() {
  return (
    <Router history={history}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <Suspense fallback={<LoadingWithSpinner />}>
          <AppComponent />
        </Suspense>
      </AppInsightsContext.Provider>
    </Router>
  );
}

function AppComponent() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = localStorage.getItem('AirswiftAccessToken');
  const { inProgress, instance, accounts } = useMsal();
  const [prevProgress, setPrevProgress] = useState(null);
  const [notification, setNotification] = useState(null);
  const appNotification = useSelector((state) => state.appNotifications?.message);
  const timesheetPlacementStatus = useSelector((state) => state?.timesheet?.validate?.timesheet.IsValidPlacementStatus);
  const expensePlacementStatus = useSelector((state) => state?.timesheet?.validate?.expense.IsValidPlacementStatus);
  const userData = useSelector((state) => state.user?.profile);
  const accessTo = useSelector((state) => state.accessToParts);
  const accessRef = useRef(accessTo);
  const [searchParams] = useSearchParams();
  const idTokenHint = searchParams.get('id_token_hint');
  const [backendAuthorized, setBackendAuthorized] = useState(false);
  const loginJustHappened = useRef(false);

  // PWA State
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);

  // Monitor network status
  useNetworkStatus();

  // Set when the user clicks "Update" in the installed app, so only that action reloads the page
  const userRequestedUpdate = useRef(false);
  const updateServiceWorkerRef = useRef(null);

  // Register service worker. The registration is shared by the browser tab and the installed app,
  // so the update prompt is only shown in the installed app; browser tabs update silently.
  const { updateServiceWorker } = useRegisterSW({
    onRegistered(r) {
      console.log('SW Registered:', r);
    },
    onRegisterError(error) {
      console.log('SW registration error:', error);
    },
    onNeedRefresh() {
      if (!isStandalone()) {
        // Activate the new version without reloading; it is picked up on the next refresh/visit
        console.log('New content available - activating silently in browser');
        updateServiceWorkerRef.current?.();
        return;
      }
      console.log('New content available');
      dispatch(setUpdateAvailable(true));
      setShowUpdateAlert(true);
    },
    onNeedReload() {
      // Replaces the plugin's automatic reload when the new service worker takes control
      if (userRequestedUpdate.current) {
        window.location.reload();
      }
    }
  });
  updateServiceWorkerRef.current = updateServiceWorker;

  // After a silent update, old lazy-loaded chunks may no longer exist on the server; reload once to recover
  useEffect(() => {
    const handlePreloadError = (event) => {
      if (sessionStorage.getItem(PRELOAD_RELOAD_KEY)) return;
      event.preventDefault();
      sessionStorage.setItem(PRELOAD_RELOAD_KEY, '1');
      window.location.reload();
    };
    const clearReloadFlag = setTimeout(() => sessionStorage.removeItem(PRELOAD_RELOAD_KEY), 10000);
    window.addEventListener('vite:preloadError', handlePreloadError);
    return () => {
      clearTimeout(clearReloadFlag);
      window.removeEventListener('vite:preloadError', handlePreloadError);
    };
  }, []);

  const handleUpdate = () => {
    setShowUpdateAlert(false);
    userRequestedUpdate.current = true;
    updateServiceWorker(true);
  };

  const handleUpdateLater = () => {
    setShowUpdateAlert(false);
    dispatch(setUpdateAvailable(false));
  };

  const callBackendAuthorization = async (accessToken) => {
    try {
      logBackendStep('Authorization_Start', { timestamp: Date.now() });
      localStorage.setItem(ACCESS_TOKEN_NAME, accessToken);
      const authResponse = await backendAuth();

      logBackendStep('Authorization_Response', {
        status: authResponse?.status,
        user: authResponse?.data?.Data?.Id
      });
      const {
        status,
        data: {
          Data: { FirstName, LastName, Id, IsFirstLogin }
        }
      } = authResponse;
      if (status === 200) {
        localStorage.setItem('AirswiftFirstName', FirstName);
        localStorage.setItem('AirswiftLastName', LastName);
        localStorage.setItem('UserId', Id);
        dispatch(fetchUserData(Id));
        logBackendStep('Authorization_Success', { userId: Id });
      } else {
        logBackendStep('Authorization_Non200', { status });
      }
      return { Id, IsFirstLogin };
    } catch (error) {
      logAppError(error, 'BackendAuthorization');
    }
  };

  useEffect(() => {
    setNotification(appNotification);
  }, [appNotification]);

  const fetchConstants = () => {
    const claims = accounts[0]?.idTokenClaims;
    if ((claims && claims[idTokenClaims.IS_TNE_CONTRACTOR]) || (claims && claims[idTokenClaims.IS_TNE_APPROVER])) {
      dispatch(getConstants(true));
    } else {
      dispatch(getConstants());
    }
  };

  useEffect(() => {
    const claims = accounts[0]?.idTokenClaims;
    fetchConstants();
    if (claims) {
      dispatch(
        setAllowedParts({
          onboarding: claims[idTokenClaims.IS_TIFO_USER],
          tAndEContractor: claims[idTokenClaims.IS_TNE_CONTRACTOR],
          tAndEApprover: claims[idTokenClaims.IS_TNE_APPROVER]
        })
      );
    }
  }, [accounts]);

  useEffect(() => {
    if (userData?.Id) {
      const { IsProfileCompleted, Id, IsFirstLogin } = userData;
      const claims = accounts[0]?.idTokenClaims;

      localStorage.setItem('userProfileCompleted', IsProfileCompleted);

      const jobForSaving = localStorage.getItem('saveJobAfterLogin');
      const jobToApply = localStorage.getItem('OpenedJobId');

      if (jobForSaving) {
        api.post(`v1/job/save-job`, { JobId: JSON.parse(jobForSaving), UserId: Id });
        setTimeout(() => localStorage.removeItem('saveJobAfterLogin'), 100);
        return;
      }

      if (jobToApply) {
        navigate(`/dashboard/jobs/${JSON.parse(jobToApply)}?action=apply`);
        return;
      }

      if (
        claims &&
        !claims[idTokenClaims.IS_TNE_APPROVER] &&
        IsFirstLogin &&
        !IsProfileCompleted &&
        !checkOnboardingCookie()
      ) {
        navigate('/my-profile');
      }
    }
  }, [userData]);

  useEffect(() => {
    const ignoreCorsErrorsFrom = [
      'px.ads.linkedin.com, google-analytics.com',
      'api.stonly.com',
      'analytics.google.com'
    ];

    const handleError = (event) => {
      if (event.filename && ignoreCorsErrorsFrom.some((domain) => event.filename.includes(domain))) {
        event.preventDefault();
      }
    };

    const handlePromiseRejection = (event) => {
      if (
        event.reason &&
        event.reason.message &&
        ignoreCorsErrorsFrom.some((domain) => event.reason.message.includes(domain))
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
  }, []);

  useEffect(() => {
    const callbackId = instance.addEventCallback(async (message) => {
      logAuthStep('Event_Received', { eventType: message.eventType });
      if (message.eventType === EventType.LOGIN_SUCCESS && message.payload?.accessToken) {
        logAuthStep('Login_Success', { hasAccessToken: true });
        try {
          await callBackendAuthorization(message.payload.accessToken);
          setBackendAuthorized(true);
          loginJustHappened.current = true;
          logAuthStep('BackendAuth_Login_Success', { userId: localStorage.getItem('UserId') });
        } catch (error) {
          logAppError(error, 'BackendAuth_Login_Success');
        }
      } else if (message.eventType === EventType.LOGIN_FAILURE) {
        logAppError(message.error, 'Login_Failure');
      }
    });

    logAuthStep('Callback_Registered');

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, []);

  useEffect(() => {
    const handleSilentAuth = async () => {
      if (
        backendAuthorized &&
        loginJustHappened.current &&
        !isAuthenticated &&
        localStorage.getItem('UserId') &&
        inProgress !== InteractionStatus.None &&
        !accounts[0]
      ) {
        try {
          logAuthStep('SilentAuth_Start', { account: accounts[0]?.username });
          const response = await instance.acquireTokenSilent({
            scopes: loginRequest.scopes,
            account: accounts[0]
          });
          logAuthStep('SilentAuth_TokenAcquired');
          await callBackendAuthorization(response.accessToken);
          setBackendAuthorized(true);
        } catch (error) {
          logAppError(error, 'SilentAuth');
        }
      }
    };
    handleSilentAuth();
  }, [isAuthenticated, inProgress, accounts, backendAuthorized]);

  useEffect(() => {
    const id = instance.addEventCallback((message) => {
      logAppEvent('Auth MSAL Event', message.eventType, SeverityLevel.Information);
    });
    return () => instance.removeEventCallback(id);
  }, []);

  const isAuthTransitioning =
    inProgress === InteractionStatus.Logout || inProgress === InteractionStatus.Login;

  return (
    <div className="App">
      <div id="root-portal" />

      {isAuthTransitioning && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff'
          }}
        >
          <LoadingWithSpinner />
        </div>
      )}

      <Suspense fallback={<LoadingWithSpinner />}>
        <Routes>
          <Route element={<QuickApplyLayout />}>
            <Route path="/quickapply-embedded/:id" element={<QuickApplyEmbedded />} />
          </Route>
          <Route element={<MainLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/jobs" element={<CandidatePortal />}>
              <Route path="" element={<Jobs />} />
              <Route path=":id">
                <Route path="" element={<Jobs isFullPage />} />
              </Route>
            </Route>
            <Route path="/interaction" element={<InteractionPage isLoggedIn={isAuthenticated} />} />
            <Route path="/onboardingInteraction" element={<OnboardingInteraction />} />
            <Route
              path="my-profile"
              element={
                accessTo?.tAndEApprover ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <AuthenticatedTemplate>
                    <CandidatePortal />
                  </AuthenticatedTemplate>
                )
              }
            >
              <Route path="" element={<MyProfile />} />
              <Route path="rtbf" element={<RightToBeForgotten />} />
            </Route>
            <Route
              path="dashboard"
              element={
                <AuthenticatedTemplate>
                  <CandidatePortal />
                </AuthenticatedTemplate>
              }
            >
              <Route path="" element={<Dashboard />} />
              <Route path="jobs">
                <Route path="" element={accessTo?.tAndEApprover ? <Navigate to="/dashboard" /> : <Jobs />} />
                <Route path=":id">
                  <Route path="" element={<Jobs isFullPage />} />
                </Route>
                <Route path="unsubscribe/:id">
                  <Route path="" element={<UnsubscribePage />} />
                </Route>
              </Route>
              <Route path="settings">
                <Route path="" element={<Settings />} />
              </Route>
              <Route path="tifo" element={accessTo?.onboarding ? <Tifo /> : <Navigate to="/dashboard" />} />
              <Route
                path="timesheets"
                element={
                  accessTo?.tAndEContractor || accessTo?.tAndEApprover ? (
                    <AuthenticatedTemplate>
                      <TimesheetsPage />
                    </AuthenticatedTemplate>
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="create-timesheet"
                element={
                  accessTo?.tAndEContractor || timesheetPlacementStatus ? (
                    <AuthenticatedTemplate>
                      <CreateTNEPage type={generalEnums.TIMESHEET} />
                    </AuthenticatedTemplate>
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="create-claim"
                element={
                  accessTo?.tAndEContractor || expensePlacementStatus ? (
                    <AuthenticatedTemplate>
                      <CreateTNEPage type={generalEnums.EXPENSE} />
                    </AuthenticatedTemplate>
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="expenses"
                element={
                  accessTo?.tAndEContractor || accessTo?.tAndEApprover ? (
                    <AuthenticatedTemplate>
                      <ExpensesPage />
                    </AuthenticatedTemplate>
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="timesheet/:id"
                element={
                  accessTo?.tAndEContractor || accessTo?.tAndEApprover ? (
                    <AuthenticatedTemplate>
                      <TimesheetPage />
                    </AuthenticatedTemplate>
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="expense/:id"
                element={
                  accessTo?.tAndEContractor || accessTo?.tAndEApprover ? (
                    <AuthenticatedTemplate>
                      <ExpensePage />
                    </AuthenticatedTemplate>
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="profile-builder-one"
                element={<Navigate to="/dashboard" />}
              />
              <Route
                path="profile-builder-two"
                element={
                  !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                    <ProfileBuilderTwo />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="profile-builder-four"
                element={
                  !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                    <ProfileBuilderFour />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="profile-builder-five"
                element={
                  !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                    <ProfileBuilderFive />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="profile-builder-six"
                element={
                  !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                    <ProfileBuilderSix />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route path="profile-builder-seven" element={<ProfileBuilderSeven />} />
            </Route>
            <Route path="/500" element={<ErrorPage />} />
            <Route path="*" element={<ErrorPage />} />
          </Route>
        </Routes>
      </Suspense>

      <InstallPWA />
      <OfflineBanner />
      <Snackbar
        open={showUpdateAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 70, sm: 24 } }}
      >
        <Alert
          severity="info"
          action={
            <>
              <Button
                color="inherit"
                size="small"
                onClick={handleUpdate}
                sx={{ fontWeight: 'bold' }}
              >
                Update
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={handleUpdateLater}
              >
                Later
              </Button>
            </>
          }
          sx={{
            backgroundColor: '#2089BE',
            color: '#fff',
            '& .MuiAlert-icon': {
              color: '#fff'
            }
          }}
        >
          New version available! Update for the latest features.
        </Alert>
      </Snackbar>

      {!isAuthenticated && <AppNotification notification={notification} isDrawerOpen={false} />}
    </div>
  );
}

export default App;